import { computed, inject, Injectable, signal } from '@angular/core';
import {  UtilService } from './util.service';
import { BrandsFiltersResponse, CompetitionFiltersResponse } from '../components/my-brand-screen/types';
import { PosthogService } from './posthog.service';
import { PosthogEventName, PosthogEventCode } from './types';
import { DateRangeValue, getLastNumDays, getThisMonth, getLastMonth, getThisQuarter, getLastQuarter, hasCompletedDays, subtractDays } from '../utils/time';
import { FixedDateRanges } from '../shared/components/date-picker/date-picker.component';
import { CommonDashboardFilterResponse, DeviceFilterType } from './types';
import { SelectItem } from '../shared/components/select-menu/select-menu.component';
import {StorageService} from './storage.service';
import {LocalStorageKeys} from '../shared/types/storage';
import {finalize, Observable, tap} from 'rxjs';
import {BrandsService} from './brands.service';
import {StateService} from './state.service';
import {AuthorizationService} from './authorization.service';
import { GroupingTypeKey, GroupingTypeLabel } from '../components/messaging-screen/types';
import { BrandLineColors, MY_BRAND_COLOR, WIDGET_COLOR_SCALE, WIDGET_LINE_COLOR_SCALE } from '../shared/types/common';

export interface DashboardStoredFilters {
  myBrandKey?: string;
  myCompetitorKeys?: string[];
  userRelatedBrand?: string;
  dateRange?: DateRangeValue;
  countries?: string[];
  device?: DeviceFilterType;
  platforms?: string[];
}

export interface MyBrandAndCompetitors {
  myBrand: SelectBrandItem;
  myCompetitors: SelectBrandItem[];
}

export interface DashboardFilters {
  userRelatedBrand: SelectBrandItem;
  myBrand: SelectBrandItem;
  myCompetitors: SelectBrandItem[];
  dateRange: DateRangeValue;
  countries: string[];
  device: DeviceFilterType;
}

export interface SocialDashboardFilters {
  userRelatedBrand: SelectBrandItem;
  myBrand: SelectBrandItem;
  myCompetitors: SelectBrandItem[];
  dateRange: DateRangeValue;
  platforms: string[];
}

export interface CountriesPayload {
  includeOtherCountries: boolean;
  countries: string[];
}

export interface DashboardFiltersPayload extends DateRangeValue, CountriesPayload {
  accountKey: string;
  devices: string[];
}

export interface MessagingFiltersPayload extends DashboardFiltersPayload {
  brandKey: string;
  competitorsKeys: string[];
  offset: number;
  limit: number;
  groupingType: GroupingTypeKey;
}

export interface MessagingGroupingFiltersPayload extends DashboardFiltersPayload {
  brandKey: string;
  competitorsKeys: string[];
  groupingType: GroupingTypeKey;
}

export interface SocialDashboardFiltersPayload extends DateRangeValue {
  accountKey: string;
  platforms: string[];
}

interface BrandCompetitors {
  [myBrandKey: string]: {
    topCompetitorKeys: string[];
    competitors: SelectBrandItem[];
  };
}

interface BrandsOptions {
  myBrands?: SelectBrandItem[];
  brandCompetitorsResponse?: BrandCompetitors;
  myCompetitors?: SelectBrandItem[];
  myBrandsAndCompetitors?: MyBrandAndCompetitors[];
  countries: SelectItem[];
}

export interface SelectBrandItemGroup {
  label: string;
  order: number;
}

export interface SelectBrandItem {
  label: string;
  key: string;
  color?: string;
  icon?: string;
  group?: SelectBrandItemGroup;
}

/** Presets that exceed the 30 days of data available to trial users. */
const TRIAL_RESTRICTED_DATE_RANGES: FixedDateRanges[] = [
  FixedDateRanges.LAST_MONTH,
  FixedDateRanges.THIS_QUARTER,
  FixedDateRanges.LAST_QUARTER,
];

@Injectable({
  providedIn: 'root',
})

export class DashboardService {
  private utilService = inject(UtilService);
  private posthogService = inject(PosthogService);
  private storageService = inject(StorageService);
  private brandsService = inject(BrandsService);
  private stateService = inject(StateService);
  private authorizationService = inject(AuthorizationService);

  public brandColorsMap = signal<Record<string, string>>({});
  public brandLineColorsMap = signal<Record<string, string>>({});
  public brandColorsByNameMap = signal<Record<string, string>>({});

  public competitionFiltersResponse?: CompetitionFiltersResponse;
  public commonFiltersResponse?: CommonDashboardFilterResponse;

  public brandsFilters = signal<DashboardFilters>({
    userRelatedBrand: {} as SelectBrandItem,
    myBrand: {} as SelectBrandItem,
    myCompetitors: [],
    dateRange: (() => this.getLocalStoredFilters().dateRange || getLastNumDays(30))(),
    device: DeviceFilterType.ALL_DEVICES,
    countries: []
  })
  private selectedPlatforms = signal<string[]>(this.getLocalStoredFilters().platforms ?? []);
  readonly platforms = computed(() => this.selectedPlatforms());

  readonly socialBrandsFilters = computed<SocialDashboardFilters>(() => ({
    ...this.brandsFilters(),
    platforms: this.selectedPlatforms(),
  }));

  public brandsOptions = signal<BrandsOptions>({
    myBrands: undefined,
    myCompetitors: [],
    brandCompetitorsResponse: undefined,
    countries: [],
    myBrandsAndCompetitors: [],
  });

  readonly myBrand = computed(() => this.brandsFilters().myBrand);
  readonly myCompetitors = computed(() => this.brandsFilters().myCompetitors);
  readonly myBrandOptions = computed(() => this.brandsOptions().myBrands);
  readonly myCompetitorsOptions = computed(() => this.brandsOptions().myCompetitors);
  readonly myBrandsAndCompetitors = computed(() => this.brandsOptions().myBrandsAndCompetitors);

  readonly userRelatedBrand = computed(() => this.brandsFilters().userRelatedBrand);
  readonly userRelatedBrandOptions = computed(() => this.brandsOptions().myBrands);

  readonly dateRange = computed(() => ({...this.brandsFilters().dateRange}));
  readonly countriesOptions = computed(() => this.brandsOptions().countries);
  readonly device = computed(() => this.brandsFilters().device);
  readonly countries = computed(() => this.brandsFilters().countries);

  readonly minStartDate = new Date('2025-07-01');

  public getBrandIcon(brandKey: string): string {
    const allBrands = (this.myBrandsAndCompetitors() ?? [])
      .flatMap(entry => [entry.myBrand, ...entry.myCompetitors]);
    return allBrands.find(brand => brand.key === brandKey)?.icon ?? '';
  }

  public getDashboardFiltersPayload(dashboardFilters?: DashboardFilters): DashboardFiltersPayload {
    const filters = dashboardFilters || this.brandsFilters();
    const validatedDateRange = this.validateDateRangeForTrialUsers(filters.dateRange);
    return {
      startDate: validatedDateRange.startDate,
      endDate: validatedDateRange.endDate,
      ...this.getCountries(filters.countries),
      devices: this.getDevices(filters.device),
      accountKey:this.stateService.activeAccount()
    };
  }

  public getSocialDashboardFiltersPayload(socialDashboardFilters?: SocialDashboardFilters): SocialDashboardFiltersPayload {
    const filters = socialDashboardFilters || this.socialBrandsFilters();
    const validatedDateRange = this.validateDateRangeForTrialUsers(filters.dateRange);
    return {
      startDate: validatedDateRange.startDate,
      endDate: validatedDateRange.endDate,
      accountKey:this.stateService.activeAccount(),
      platforms: filters.platforms
    };
  }

  private getCountries(countries: string[]): CountriesPayload {
    const isOtherIncluded = countries.includes('other');
    return {
      includeOtherCountries: isOtherIncluded,
      countries: isOtherIncluded ? countries.filter(country => country !== 'other') : countries
    };
  }

  private getDevices(deviceType: DeviceFilterType): string[] {
    switch (deviceType) {
      case DeviceFilterType.MOBILE:
        return ['mobile', 'tablet'];
      case DeviceFilterType.DESKTOP:
        return ['desktop'];
      default:
        return []; // all devices
    }
  }

  public setInitialBrandsFilters(competitionFilters: CompetitionFiltersResponse): void {
    this.competitionFiltersResponse = competitionFilters;
    const localFilters = this.getLocalStoredFilters();
    this.setBrandsOptions(competitionFilters.brands);
    this.updateBrandsFilters(localFilters);
    this.setBrandsColorsMaps();
  }

  public setInitialCommonFilters(commonFilters: CommonDashboardFilterResponse): void {
    this.commonFiltersResponse = commonFilters;

    const { countries } = commonFilters;
    const options = Object.keys(countries).map(countryCode => ({
        label: countries[countryCode],
        key: countryCode,
        icon: this.utilService.getCountryFlagUrl(countryCode)
    }));
    const otherOption = {
      label: 'Other',
      key: 'other',
      icon: '/svg/globe.svg'
    };
    options.push(otherOption);

    this.brandsOptions.update(f => ({ ...f, countries: options }));
  }

  private getLocalStoredFilters(): DashboardStoredFilters {
    const localFilters = this.storageService.local.getItem(LocalStorageKeys.DASHBOARD_FILTERS);
    const dateRange = this.processStoredDateRange(localFilters?.dateRange);
    return {
      myBrandKey: localFilters?.myBrandKey,
      myCompetitorKeys: localFilters?.myCompetitorKeys,
      userRelatedBrand: localFilters?.userRelatedBrand,
      dateRange,
      platforms: localFilters?.platforms,
    };
  }

  /** Normalizes and validates date range from storage (legacy number, relative, or absolute). */
  private processStoredDateRange(raw: unknown): DateRangeValue | undefined {
    let dateRange: DateRangeValue | undefined;

    // TODO: remove support for old number format
    if (typeof raw === 'number') {
      dateRange = getLastNumDays(raw);
    } else if (raw && typeof raw === 'object' && 'startDate' in raw) {
      dateRange = raw as DateRangeValue;
    }

    if (!dateRange) {
      return undefined;
    }

    if (dateRange.startDate) {
      const startDate = new Date(dateRange.startDate as string | Date);
      if (startDate < this.minStartDate) {
        return undefined;
      }
    }

    if (dateRange.relativeDate) {
      dateRange = this.recalculateDateRangeFromRelative(dateRange.relativeDate);
    } else if (dateRange.startDate && dateRange.endDate) {
      dateRange = this.normalizeDateRange({ ...dateRange, relativeDate: undefined });
    }

    dateRange = this.validateDateRangeForTrialUsers(dateRange);
    return this.normalizeDateRange(dateRange);
  }

  /** Ensures startDate/endDate are Date instances (e.g. after JSON parse from storage). */
  private normalizeDateRange(dateRange: DateRangeValue): DateRangeValue {
    return {
      ...dateRange,
      startDate: dateRange.startDate != null
        ? (dateRange.startDate instanceof Date ? dateRange.startDate : new Date(dateRange.startDate as string))
        : null,
      endDate: dateRange.endDate != null
        ? (dateRange.endDate instanceof Date ? dateRange.endDate : new Date(dateRange.endDate as string))
        : null,
    };
  }

  private recalculateDateRangeFromRelative(relativeDate: FixedDateRanges): DateRangeValue {
    if (this.authorizationService.isTrialUser && TRIAL_RESTRICTED_DATE_RANGES.includes(relativeDate)) {
      return getLastNumDays(30);
    }

    const dateRange = this.resolveRelativeDateRange(relativeDate);
    // A stored "this month"/"this quarter" preset holds no days yet when restored on the
    // period's first day, so fall back rather than request an empty range.
    return hasCompletedDays(dateRange) ? dateRange : getLastNumDays(30);
  }

  private resolveRelativeDateRange(relativeDate: FixedDateRanges): DateRangeValue {
    switch (relativeDate) {
      case FixedDateRanges.YESTERDAY:
        return getLastNumDays(1);
      case FixedDateRanges.LAST_7_DAYS:
        return getLastNumDays(7);
      case FixedDateRanges.LAST_14_DAYS:
        return getLastNumDays(14);
      case FixedDateRanges.LAST_30_DAYS:
        return getLastNumDays(30);
      case FixedDateRanges.THIS_MONTH:
        return getThisMonth();
      case FixedDateRanges.LAST_MONTH:
        return getLastMonth();
      case FixedDateRanges.THIS_QUARTER:
        return getThisQuarter();
      case FixedDateRanges.LAST_QUARTER:
        return getLastQuarter();
      default:
        return getLastNumDays(30);
    }
  }

  private setLocalFilters(): void {
    this.storageService.local.setItem(LocalStorageKeys.DASHBOARD_FILTERS, {
      myBrandKey: this.brandsFilters().myBrand.key,
      myCompetitorKeys: this.brandsFilters().myCompetitors.map(c => c.key),
      userRelatedBrand: this.brandsFilters().userRelatedBrand.key,
      dateRange: this.brandsFilters().dateRange,
    });
  }

  private setSocialLocalFilters(): void {
    this.setLocalFilters();
    const storedFilters = this.storageService.local.getItem(LocalStorageKeys.DASHBOARD_FILTERS);
    this.storageService.local.setItem(LocalStorageKeys.DASHBOARD_FILTERS, {
      ...storedFilters,
      platforms: this.selectedPlatforms(),
    });
  }

  private setBrandsColorsMaps(): void {
    const { myBrand, myCompetitors } = this.brandsFilters();
    if (myBrand) {
      this.brandColorsByNameMap.set({[myBrand.label]: MY_BRAND_COLOR});
      this.brandColorsMap.set({[myBrand.key]: MY_BRAND_COLOR});
      this.brandLineColorsMap.set({[myBrand.key]: BrandLineColors.MY_BRAND});
    }

    if (myCompetitors) {
      for (const [index, competitor] of myCompetitors.entries()) {
        const color = WIDGET_COLOR_SCALE[index % WIDGET_COLOR_SCALE.length];
        const lineColor = WIDGET_LINE_COLOR_SCALE[index % WIDGET_LINE_COLOR_SCALE.length];
        competitor.color = color;
        this.brandColorsByNameMap.update(f => ({...f, [competitor.label]: color}));
        this.brandColorsMap.update(f => ({...f, [competitor.key]: color}));
        this.brandLineColorsMap.update(f => ({...f, [competitor.key]: lineColor}));
      }
    }
  }

  private setBrandsOptions(brands: BrandsFiltersResponse): void {
    this.brandsOptions.update(f => ({
      ...f,
      myBrands: Object.keys(brands).map(brandKey => {
        const brand = brands[brandKey];
        return {
          label: brand.name,
          key: brandKey,
          icon: brand.logoPath,
          color: MY_BRAND_COLOR // my brands will always have the same color
        };
      }),
      brandCompetitorsResponse: Object.keys(brands).reduce((acc, brandKey) => {
        const brand = brands[brandKey];
        acc[brandKey] = {
          topCompetitorKeys: brand.topCompetitorKeys,
          competitors: Object.keys(brand.competitors).map(competitorKey => {
            const competitor = brand.competitors[competitorKey];
            return {
              label: competitor?.name,
              key: competitorKey,
              icon: competitor.logoPath,
            };
          }),
        };
        return acc;
      }, {} as BrandCompetitors),
      myBrandsAndCompetitors: Object.keys(brands).map(brandKey => {
        const brand = brands[brandKey];
        const brandItem = this.createBrandItem(brandKey, brand, MY_BRAND_COLOR);
        const competitors = Object.keys(brand.competitors).map(competitorKey => {
          const competitor = brand.competitors[competitorKey];
          return this.createBrandItem(competitorKey, competitor);
        });
        return {
          myBrand: brandItem,
          myCompetitors: competitors,
        } as MyBrandAndCompetitors;
      }),
    }));
  }

  private createBrandItem(key: string, brand: any, color?: string, group?: SelectBrandItemGroup): SelectBrandItem {
    return {
      label: brand.name,
      key,
      icon: brand.logoPath,
      ...(color ? { color } : {}),
      ...(group ? { group } : {}),
    };
  }

  private getTopCompetitors(myBrandKey: string): SelectBrandItem[] {
    const { topCompetitorKeys, competitors } = this.brandsOptions()?.brandCompetitorsResponse?.[myBrandKey] || {};
    const topCompetitors = topCompetitorKeys
      ?.map(competitorKey => competitors?.find(c => c.key === competitorKey))
      ?.filter(c => c !== undefined);
      //@ts-ignore
    return topCompetitors || [];
  }

  public getAllCompetitorsByBrandKey(brandKey: string): SelectBrandItem[] {
    const { competitors } = this.brandsOptions()?.brandCompetitorsResponse?.[brandKey] || {};
    return competitors || [];
  }

  private getCompetitorsBySessionFilters(myBrandKey: string, competitorKeys?: string[]): SelectBrandItem[] | undefined {
    if (competitorKeys?.length) {
      const { competitors } = this.brandsOptions()?.brandCompetitorsResponse?.[myBrandKey] || {};
      const filteredCompetitors = competitors?.filter(c => competitorKeys.includes(c.key));
      return filteredCompetitors?.length ? filteredCompetitors : undefined;
    }
    return undefined;
  }

  private updateBrandsFilters(sessionFilters: DashboardStoredFilters): void {
    const { myBrandKey: sessionBrandKey, myCompetitorKeys: sessionCompetitorsKeys, userRelatedBrand: sessionUserRelatedBrand } = sessionFilters;
    const { myBrands } = this.brandsOptions();

    const myBrand = myBrands?.find(b => b.key === sessionBrandKey) || myBrands?.[0];
    if (myBrand?.key) {
      const sessionCompetitors = this.getCompetitorsBySessionFilters(myBrand.key, sessionCompetitorsKeys);
      const competitors = sessionCompetitors?.length ? sessionCompetitors : this.getTopCompetitors(myBrand.key);
      this.brandsFilters.update(f => ({ ...f, myBrand, myCompetitors: competitors }));
      this.brandsOptions.update(f => ({ ...f, myCompetitors: this.getMyCompetitorOptions() }));
    }

    const userRelatedBrand = myBrands?.find(b => b.key === sessionUserRelatedBrand) || myBrands?.[0];
    if (userRelatedBrand?.key) {
      this.brandsFilters.update(f => ({ ...f, userRelatedBrand }));
    }

    this.onFilterChange();
  }

  public updateMyBrand(selectedBrandKey: string): void {
    this.posthogService.capture(PosthogEventName.FILTERED_BRANDS, { [PosthogEventCode.MY_BRAND]: selectedBrandKey});
    const { myBrands } = this.brandsOptions();
    const myBrand = myBrands?.find(b => b.key === selectedBrandKey);
    if (myBrand?.key) {
      this.brandsFilters.update(f => ({ ...f, myBrand, myCompetitors: this.getTopCompetitors(myBrand.key) }));
      this.brandsOptions.update(f => ({ ...f, myCompetitors: this.getMyCompetitorOptions() }));
      this.onFilterChange();
    }
  }

  public updateCompetitor(competitorKey: string, previousCompetitorKey: string): void {
    const { myBrand, myCompetitors } = this.brandsFilters();
    if (myBrand?.key) {
      const { brandCompetitorsResponse } = this.brandsOptions();
      const { competitors: competitorsResponse } = brandCompetitorsResponse?.[myBrand?.key] || {};
      if (myCompetitors && competitorsResponse) {
        const newCompetitor = competitorsResponse.find(c => c.key === competitorKey);
        if (newCompetitor) {
          const newCompetitors = myCompetitors.map(c => c.key === previousCompetitorKey ? newCompetitor : c );
          this.brandsFilters.update(f => ({ ...f, myCompetitors: newCompetitors }));
          this.brandsOptions.update(f => ({ ...f, myCompetitors: this.getMyCompetitorOptions() }));
          this.posthogService.capture(PosthogEventName.FILTERED_BRANDS, { [PosthogEventCode.MY_BRAND]: myBrand.key, [PosthogEventCode.MY_COMPETITORS]: this.getCompetitorsBrandKeys(newCompetitors)});
          this.onFilterChange();
        }
      }
    }
  }

  public removeCompetitor(competitorKey: string): void {
    const { myBrand, myCompetitors } = this.brandsFilters();
    if (myBrand?.key &&  myCompetitors) {
      const newCompetitors = myCompetitors.filter(c => c.key !== competitorKey);
      this.brandsFilters.update(f => ({ ...f, myCompetitors: newCompetitors }));
      this.brandsOptions.update(f => ({ ...f, myCompetitors: this.getMyCompetitorOptions() }));
      this.posthogService.capture(PosthogEventName.FILTERED_BRANDS, { [PosthogEventCode.MY_BRAND]: myBrand.key, [PosthogEventCode.MY_COMPETITORS]: this.getCompetitorsBrandKeys(newCompetitors)});
      this.onFilterChange();
    }
  }

  public addCompetitor(competitorKey: string): void {
    const { myBrand, myCompetitors } = this.brandsFilters();
    if (myBrand?.key && myCompetitors) {
      const { brandCompetitorsResponse } = this.brandsOptions();
      const newCompetitor = brandCompetitorsResponse?.[myBrand.key].competitors
        .find(c => c.key === competitorKey);
      if (newCompetitor) {
        const newCompetitors = [...myCompetitors, newCompetitor];
        this.brandsFilters.update(f => ({ ...f, myCompetitors: newCompetitors }));
        this.brandsOptions.update(f => ({ ...f, myCompetitors: this.getMyCompetitorOptions() }));
        this.posthogService.capture(PosthogEventName.FILTERED_BRANDS, { [PosthogEventCode.MY_BRAND]: myBrand.key, [PosthogEventCode.MY_COMPETITORS]: this.getCompetitorsBrandKeys(newCompetitors)});
        this.onFilterChange();
      }
    }
  }

  public setCompetitors(competitorKeys: string[]): void {
    const { myBrand } = this.brandsFilters();
    if (myBrand?.key) {
      const { brandCompetitorsResponse } = this.brandsOptions();
      const allCompetitors = brandCompetitorsResponse?.[myBrand.key]?.competitors || [];
      const newCompetitors = competitorKeys
        .map(key => allCompetitors.find(c => c.key === key))
        .filter((c): c is SelectBrandItem => !!c);
      if (newCompetitors.length) {
        this.brandsFilters.update(f => ({ ...f, myCompetitors: newCompetitors }));
        this.brandsOptions.update(f => ({ ...f, myCompetitors: this.getMyCompetitorOptions() }));
        this.onFilterChange();
      }
    }
  }

  public updateUserRelatedBrand(userRelatedBrandKey: string): void {
    const { myBrands } = this.brandsOptions();
    const userRelatedBrand = myBrands?.find(b => b.key === userRelatedBrandKey);
    if (userRelatedBrand?.key) {
      this.brandsFilters.update(f => ({ ...f, userRelatedBrand }));
    }
    this.setLocalFilters();
  }

  private getMyCompetitorOptions(): SelectBrandItem[] {
    const { myBrand, myCompetitors: currentCompetitors } = this.brandsFilters();
    if (myBrand?.key) {
      const { brandCompetitorsResponse } = this.brandsOptions();
      const myCompetitorsOptions = brandCompetitorsResponse?.[myBrand?.key].competitors;
      const filteredCompetitors = myCompetitorsOptions
        ?.filter(competitor => !currentCompetitors?.some(c => c.key === competitor.key));
      return filteredCompetitors || [];
    }
    return [];
  }

  private onFilterChange(): void {
    this.setBrandsColorsMaps();
    this.setLocalFilters();
  }

  private getCompetitorsBrandKeys(brands: SelectBrandItem[]): string {
    return brands.map(b => b.key).join(';');
  }

  public updateDateRange(dateRange: DateRangeValue) {
    const formatDate = (date: Date | null) => date?.toISOString().split('T')[0] ?? '';
    this.posthogService.capture(PosthogEventName.SELECTED_DATE_RANGE, {
      [PosthogEventCode.SELECTED_DATE_RANGE]: `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`,
      ...(dateRange.relativeDate && { [PosthogEventCode.RELATIVE_DATE]: dateRange.relativeDate })
    });
    this.brandsFilters.update(f => ({ ...f, dateRange }));
    this.setLocalFilters();
  }

  public updateDevice(device: DeviceFilterType) {
    this.posthogService.capture(PosthogEventName.FILTERED_DEVICE, {[PosthogEventCode.FILTERED_DEVICE]: device});
    this.brandsFilters.update(f => ({ ...f, device }));
  }

  public updateCountries(countries: string[]) {
    this.posthogService.capture(PosthogEventName.FILTERED_COUNTRY, {[PosthogEventCode.FILTERED_COUNTRY]: countries.join(';') || 'all'});
    this.brandsFilters.update(f => ({ ...f, countries }));
  }

  public updatePlatforms(platforms: string[]) {
    this.posthogService.capture(PosthogEventName.FILTERED_PLATFORM, {[PosthogEventCode.FILTERED_PLATFORM]: platforms.join(';') || 'all'});
    this.selectedPlatforms.set(platforms);
    this.setSocialLocalFilters();
  }

  public refreshFilters(): Observable<CompetitionFiltersResponse> {
    this.stateService.loading = true;
    return this.brandsService.getMyCompetitionFilters(this.dateRange(), this.stateService.activeAccount()).pipe(
      tap((competitionFilters) => {
        this.setInitialBrandsFilters(competitionFilters);
      }),
      finalize(()=>{
          this.stateService.loading = false
      })
    )
  }

  private validateDateRangeForTrialUsers(dateRange: DateRangeValue): DateRangeValue {
    if (!this.authorizationService.isTrialUser) {
      return dateRange;
    }

    if (dateRange.relativeDate && TRIAL_RESTRICTED_DATE_RANGES.includes(dateRange.relativeDate)) {
      return getLastNumDays(30);
    }

    const thirtyDaysAgo = subtractDays(new Date(), 30);
    const startDate = dateRange.startDate ? new Date(dateRange.startDate) : null;
    const endDate = dateRange.endDate ? new Date(dateRange.endDate) : null;

    if ((startDate && startDate < thirtyDaysAgo) || (endDate && endDate < thirtyDaysAgo)) {
      return getLastNumDays(30);
    }

    return dateRange;
  }

}
